/* eslint-disable react-hooks/exhaustive-deps, @typescript-eslint/no-misused-promises, @typescript-eslint/strict-boolean-expressions */

import { Box, Button, Stack } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { ToastData, Watchlist, WatchlistSaveData } from '../../types';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  watchlistsState,
  playAlertAudioState,
  userDetailsState,
} from '../../states';
import { SGModal } from '../shared/SGModal';
import { arraysEqual, arraySpliceCopy, isBloomberg } from '../../util';
import useWatchlists from '../../hooks/useWatchlists';
import { EditWatchlist } from './EditWatchlist';

type EditWatchlistsProps = {
  onClose?: () => void;
  onChange?: (data: WatchlistSaveData) => void;
  open?: boolean;
  modal: boolean;
  openToast?: (toastData: ToastData) => void;
};

const MAX_NUM_WATCHLISTS = 15;

export const EditWatchlists = ({
  onClose,
  open,
  modal,
  onChange,
  openToast = (_) => {},
}: EditWatchlistsProps) => {
  const userDetails = useRecoilValue(userDetailsState);
  const [watchlists, setWatchlists] = useRecoilState(watchlistsState);
  // newWatchlists handles number of watchlists + initial content but we do not update it on onChange
  // since that causes UI weirdness when updating/re-rendering an item the user is actively editing
  // so new watchlists is the number of watchlists + initial state + what we render initially
  // saveData is what changes as the user edits
  const [newWatchlists, setNewWatchlists] = useState(watchlists ?? []);
  // saveData will be updated with all the changes as the user edits but we do not use it to render
  const [saveData, setSaveData] = useState<WatchlistSaveData>({
    watchlists: [],
    deletes: [],
  });
  const [watchlistSaveLoading, setWatchlistSaveLoading] = useState(false);
  const [newIdx, setNewIdx] = useState(-1);
  const { saveWatchlists } = useWatchlists();
  const playAlertAudio = useRecoilValue(playAlertAudioState);

  const onCloseModal = useCallback(() => {
    setNewWatchlists(watchlists ?? []);
    setSaveData({ watchlists: [], deletes: [] });
    if (onClose != null) {
      onClose();
    }
  }, [userDetails, newWatchlists, saveData]);

  useEffect(() => {
    if (modal && !open && watchlists != null) {
      setNewWatchlists(watchlists);
    }
  }, [modal, open, watchlists]);

  useEffect(() => {
    if (onChange != null) {
      onChange(saveData);
    }
  }, [saveData]);

  const addWatchlist = useCallback(() => {
    if (newWatchlists.length >= MAX_NUM_WATCHLISTS) {
      openToast({
        message: `You've reached the maximum of ${MAX_NUM_WATCHLISTS} watchlists`,
        type: 'error',
      });
      return;
    }

    const newWatchlist: Watchlist = {
      id: newIdx,
      disabledAlertCategories: [],
      soundDisabled: !playAlertAudio,
      name: 'New Watchlist',
      symbols: [],
    };
    setNewIdx((idx) => idx - 1);
    setNewWatchlists([...newWatchlists].concat([newWatchlist]));
    setSaveData({
      watchlists: [...saveData.watchlists, newWatchlist],
      deletes: [...saveData.deletes],
    });
  }, [newIdx, newWatchlists, saveData]);

  const onWatchlistChange = (newWatchlist: Watchlist | null, index: number) => {
    const watchlist = newWatchlists[index];
    const filteredWatchlists = saveData.watchlists.filter(
      (w) => w.id !== watchlist.id,
    );
    if (newWatchlist == null) {
      // Deleted this watchlist; splice it out. Make sure to copy any saved changes over
      const id2watchlist = new Map(filteredWatchlists.map((f) => [f.id, f]));
      newWatchlists.splice(index, 1);
      setNewWatchlists(newWatchlists.map((w) => id2watchlist.get(w.id) ?? w));

      const deletes =
        Number(watchlist?.id) >= 0 // only delete pre-existing watchlists
          ? [...saveData.deletes, watchlist.id as number]
          : saveData.deletes;
      setSaveData({ watchlists: filteredWatchlists, deletes });
      return;
    }

    // EditWatchlist will make extraneous calls to onChange on initial render
    // such that nothing has actually changed.  Avoid saving this no-op
    if (
      newWatchlist.name === watchlist.name &&
      arraysEqual(newWatchlist.symbols, watchlist.symbols) &&
      newWatchlist.soundDisabled === watchlist.soundDisabled
    ) {
      return;
    }

    if (newWatchlist.name === watchlist.name) {
      // It's safe to set our newWatchlists state and not interrupt the typing
      // experience in the <TextField> of <EditWatchlist>
      setNewWatchlists(arraySpliceCopy(newWatchlists, index, newWatchlist));
    }
    setSaveData({
      watchlists: filteredWatchlists.concat([newWatchlist]),
      deletes: saveData.deletes,
    });
  };

  const onSave = useCallback(async () => {
    if (watchlistSaveLoading) {
      return;
    }

    setWatchlistSaveLoading(true);
    const response = await saveWatchlists(
      saveData.watchlists,
      saveData.deletes,
    );
    setWatchlistSaveLoading(false);
    if (Array.isArray(response)) {
      setWatchlists(response);
      setNewWatchlists(response);
      setSaveData({ watchlists: [], deletes: [] });

      openToast({
        message: `Successfully saved watchlists.`,
        type: 'success',
      });
      if (onClose != null) {
        onClose();
      }
    } else {
      openToast({
        message: `There was an error saving your watchlists. Please try again.`,
        type: 'error',
      });
    }
  }, [saveData, watchlistSaveLoading, userDetails]);

  // bloomberg users cannot edit their watchlists from our app - must use worksheets
  if (userDetails == null || isBloomberg()) {
    return null;
  }

  const body = (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box>
        <Stack spacing={4}>
          {newWatchlists.map((watchlist, i) => (
            <EditWatchlist
              key={`${watchlist.name}-${i}`}
              watchlist={watchlist}
              onChange={(newWatchlist) => onWatchlistChange(newWatchlist, i)}
              modal={modal}
            />
          ))}
        </Stack>

        <Button
          onClick={addWatchlist}
          sx={{
            textTransform: 'none',
            fontSize: '14px',
            marginTop: '15px',
          }}
        >
          + New Watchlist
        </Button>
      </Box>
    </Box>
  );

  return modal ? (
    <SGModal
      open={open}
      onClose={onCloseModal}
      onSave={onSave}
      saveLoading={watchlistSaveLoading}
      title="Edit Watchlists"
      description="Modify your watchlists. Changes won't take effect until saved."
    >
      {body}
    </SGModal>
  ) : (
    body
  );
};
